import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import {
  ProjectValidation,
  ProjectValidationUnit,
  ValidationItem
} from '../../models/data-structures/project-validation';
import { Project, ProjectPropName } from '../../models/project/project.model';
import { ProjectInfoValidation } from './validation-sub-modules/project-info.validation';
import { FeedstockValidation } from './validation-sub-modules/feedstock-validation/feedstock.validation';
import { FinancialValidation } from './validation-sub-modules/financial-validation/financial.validation';
import { GhgValidation } from './validation-sub-modules/ghg-validation/ghg.validation';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { takeUntil } from 'rxjs/operators';
import { PlantSpecificationsValidation } from './validation-sub-modules/plant-spec-validation/plant-specifications.validation';
import { BehaviorSubject } from 'rxjs';
import { LooseObject } from '../../models/loose-object.model';

@Injectable({
  providedIn: 'root',
})
export class ValidationService implements OnDestroy {
  /** A notifier that emits when the service is destroyed */
  private onDestroyNotifier = new EventEmitter();

  /**
   * A notifier for knowing when the validation service is ready
   */
  validationServiceReadyNotifier = new BehaviorSubject(false);

  /** An instance of the current project */
  private project: Project | null = null;

  private isProjectInput: boolean = false;

  /**
   * creates subscriptions that updated and set the current project when the project if loaded or saved
   * @param projectCommService
   */
  constructor(private projectCommService: ProjectCommunicationService) {
    projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.project = this.projectCommService.getProject();
          this.isProjectInput = this.projectCommService.isProjectInput();
        } else {
          this.project = null;
        }
        this.validationServiceReadyNotifier.next(loaded);
      });

    projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.project = this.projectCommService.getProject();
          this.isProjectInput = this.projectCommService.isProjectInput();
        }
      });
  }

  /**
   * triggers the on destroyed notifier and unsubs from it
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Check each feedstock in the project and determine if any are missing both
   * category, type, and source as well as ts and vs and bmp
   */
  checkFeedstockCharacteristics() {
    if (!this.project) return;
    let showMessage = false;
    for (const feedstock of this.project.feedstocks) {
      if (
        !feedstock.category && !feedstock.type && !feedstock.source
        && !feedstock.characteristics.ts.value
        && !feedstock.characteristics.vs.value
        && !feedstock.bmpPotential.type
      ) {
        showMessage = true;
      }
    }
    return showMessage;
  }

  /**
   * Get the project validation for the current project
   */
  getProjectValidation(): ProjectValidation {
    if (!this.project) {
      throw Error('unable to validate project');
    }
    const progress = new ProjectValidation();

    const validationUnit = new ProjectValidationUnit();
    this.addProjectInfoValidation(validationUnit, progress);
    this.addFeedstockValidation(validationUnit, progress);
    this.addPlantSpecValidation(validationUnit, progress);
    if (this.project.projectInfo.useFinancial) {
      this.addFinancialValidation(validationUnit, progress);
    }
    if (this.project.projectInfo.useGHG) {
      this.addGhgValidation(validationUnit, progress);
    }

    progress.complete = validationUnit.isValid;
    progress.invalidInputs = validationUnit.invalidFields;
    progress.validInputs = validationUnit.validFields;
    progress.tree = validationUnit.tree;
    return progress;
  }

  /**
   * Adds the feedstock validation to the provided validation unit, and updates the progress of the
   * provided project validation
   * @param validationUnit will have feedstock validation added to it's sub modules
   * @param progress is the overall project validation; progress will be updated with the feedstocks validation
   */
  private addFeedstockValidation(validationUnit: ProjectValidationUnit, progress: ProjectValidation) {
    if (!this.project) throw Error('unable to validate feedstock');
    const feedstocks = this.project.feedstocks;
    let validFields = 0;
    let invalidFields = 0;
    // if no feedstocks add invalid fields to inflate completion percentage
    if (feedstocks.length < 1) {
      const errMsgs = [ProjectErrors.MissingFeedstock];
      validationUnit.isValid = false;
      // plus 6 invalid fields here will represent the weight of an empty product
      validationUnit.invalidFields += 6;
      invalidFields += 6;
      (validationUnit.tree as LooseObject)[ProjectPropName.feedstock]
        = new ValidationItem(false, ProjectPropName.feedstock, errMsgs);
    }
    for (let i = 0; i < feedstocks.length; i++) {
      const prodValid = new FeedstockValidation(feedstocks[i], this.isProjectInput).getValidation(undefined, this.project);
      validFields += prodValid.validFields;
      invalidFields += prodValid.invalidFields;
      validationUnit.addArrayToModuleResults(
        prodValid,
        ProjectPropName.feedstock,
        i,
        feedstocks[i].name,
      );
    }
    progress.feedstockPercentage = 100 * (validFields / (validFields + invalidFields));
  }

  /**
   * Adds project info validation to the provided validation unit, and updates the progress of the
   * provided project validation
   * @param validationUnit is a ProjectValidationUnit that will have project info validation added
   * to its submodules
   * @param progress is the overall ProjectValidation; progress will be updated with project info validation
   */
  private addProjectInfoValidation(validationUnit: ProjectValidationUnit, progress: ProjectValidation) {
    if (this.project) {
      const validationUtil = new ProjectInfoValidation(this.project.projectInfo);
      const projectInfoValidation = validationUtil.getValidation();
      validationUnit.addModuleResults(projectInfoValidation, ProjectPropName.projectInfo);
      progress.projectInfoPercentage = 100 * (projectInfoValidation.validFields /
        (projectInfoValidation.validFields + projectInfoValidation.invalidFields)
      );
    }
  }

  /**
   * Adds plant specification validation to the provided validation unit, and updates the progress of the
   * provided project validation
   * @param validationUnit is a ProjectValidationUnit that will have the plant specification validation added
   * to its submodules
   * @param progress is the overall ProjectValidation; progress will be updated with the plant specification validation
   */
  private addPlantSpecValidation(validationUnit: ProjectValidationUnit, progress: ProjectValidation) {
    if (this.project) {
      const plantSpecValidation = new PlantSpecificationsValidation(
        this.project.plantSpecifications,
        this.isProjectInput,
      ).getValidation();
      validationUnit.addModuleResults(plantSpecValidation, ProjectPropName.plantSpecifications);
      progress.plantSpecificationsPercentage = 100 * (plantSpecValidation.validFields /
        (plantSpecValidation.validFields + plantSpecValidation.invalidFields)
      );
    }
  }

  /**
   * Adds financial validation to the provided validation unit, and updates the progress of the
   * provided project validation
   * @param validationUnit is a ProjectValidationUnit that will have financial validation added
   * to its submodules
   * @param progress is the overall ProjectValidation; progress will be updated financial validation
   */
  private addFinancialValidation(validationUnit: ProjectValidationUnit, progress: ProjectValidation) {
    if (!this.project) return;
    const financialValidation = new FinancialValidation(
      this.project,
      this.isProjectInput,
    ).getValidation();
    progress.financialPercentage = 100 * (financialValidation.validFields /
      (financialValidation.validFields + financialValidation.invalidFields));
    validationUnit.addModuleResults(financialValidation, ProjectPropName.financial);
  }

  /**
   * Adds GHG validation to the provided validation unit, and updates the progress of provided project validation
   * @param validationUnit is a ProjectValidationUnit that will have GHG validation added to its submodules
   * @param progress is the overall ProjectValidation; progress will be updated with GHG validation
   */
  private addGhgValidation(validationUnit: ProjectValidationUnit, progress: ProjectValidation) {
    if (!this.project || this.isProjectInput) {
      return;
    }

    const ghgValidation = new GhgValidation(
      this.project.ghgEmissions,
      this.project.projectInfo,
    ).getValidation();
    if(!ghgValidation) return;

    progress.ghgEmissionPercentage = 100 * (ghgValidation.validFields /
      (ghgValidation.validFields + ghgValidation.invalidFields));
    validationUnit.addModuleResults(ghgValidation, ProjectPropName.ghgEmissions);
  }
}

/**
 * An enum that contains validation error that are related to the project model
 */
enum ProjectErrors {
  MissingFeedstock = 'VALIDATION.MISSING_FEEDSTOCK',
}
